/**
 * The interactive grid view (Requirements 1.4–1.9).
 *
 * This is the imperative shell around the three pure grid modules: `geometry.ts`
 * decides which rows and columns a scroll offset shows, `tileCache.ts` holds their
 * contents, `painter.ts` draws them, and `interaction.ts` turns pointer and key
 * positions into selections, scrolls, and resize drags. Everything DOM-shaped lives
 * here and nowhere else, which is what lets those four be tested in Node.
 *
 * Five decisions worth stating:
 *
 * **One frame is one `requestAnimationFrame` callback, and it never awaits.** A
 * repaint reads the tile cache synchronously; tiles that have not arrived paint
 * blank and a later frame fills them in. This is the mechanism behind Requirement
 * 1.5's 50 ms budget: frame cost is bounded by the viewport, never by the dataset.
 *
 * **Scrollbars are drawn, not native.** 5,000,000 rows at 20 px is 100,000,000 px
 * of content, far past the height a browser will lay out, so the view owns two
 * proportional scrollbars and maps thumb position onto the scrollable extent
 * (`scrollFromThumb`).
 *
 * **Resize drags preview locally and commit once.** While the pointer moves, the
 * dragged column's width is overridden in a local axis so the user sees the width
 * they are choosing; on release, one `resizeCol`/`resizeRow` command persists it in
 * the Workbook (Requirements 1.6, 1.7). Dispatching per pointer move would put a
 * hundred undo entries in the History_Manager for one drag.
 *
 * **Invalidation comes from the kernel.** The shell forwards `changedRegions` from
 * the `changed` broadcast into `cache.invalidate`; this module never polls and never
 * guesses what a command touched.
 *
 * **A canvas is not accessible, so the canvas is not the interactive element.** The
 * canvas is `aria-hidden`, and focus lives on a sibling `role="grid"` surface that
 * carries the row and column counts, the active cell's reference and text in an
 * `aria-live` region, and every keyboard handler. A screen-reader user navigates
 * with the same arrow keys and hears each cell as it becomes active.
 */

import type { Command, CommandResult } from '../core/command/index.js';
import type { CellStylePatch } from '../core/format/style.js';
import { addrToA1, rangeToA1 } from '../core/a1.js';
import {
  MAX_COLS,
  MAX_ROWS,
  normalizeRange,
  type Addr,
  type Direction,
  type Range,
  type SheetId,
} from '../core/types.js';
import {
  beginEdit as newEditSession,
  cancelEdit as cancelEditSession,
  commitPlan,
  pointReference,
  type CommitKey,
} from '../grid/editor.js';
import {
  blockNavOf,
  commitKeyOf,
  handleKey,
  isTypeToEdit,
  type BlockNav,
  type ShortcutAction,
  type ShortcutMode,
} from '../grid/shortcuts.js';
import {
  clampScroll,
  colAxisOf,
  computeVisibleRange,
  COL_HEADER_HEIGHT_PX,
  createAxis,
  DEFAULT_BUFFER_COLS,
  DEFAULT_BUFFER_ROWS,
  offsetOf,
  ROW_HEADER_WIDTH_PX,
  rowAxisOf,
  scrollToShow,
  sizeOf,
  totalPx,
  visibleSegments,
  type Axis,
} from '../grid/geometry.js';
import {
  beginResize,
  cursorFor,
  extendTo,
  hitFillHandle,
  hitTest,
  isNavKey,
  navigate,
  resizeSize,
  scrollByWheel,
  scrollFromThumb,
  selectCell,
  selectColumn,
  selectRow,
  thumbFraction,
  type ResizeDrag,
  type SelectionState,
} from '../grid/interaction.js';
import { fillDirection, fillHandleTarget } from '../core/clipboard/index.js';
import { paintGrid, type GridContext2D, type PaintStats } from '../grid/painter.js';
import { createTileCache, type TileCache } from '../grid/tileCache.js';
import { styleOf, type CellContent, type SheetView, type ViewTile } from '../grid/viewTile.js';
import { createCellEditor, type CellEditor, type EditorRect } from './cellEditor.js';
import { el } from './dom.js';

/** Width of the drawn scrollbars, in CSS pixels. */
export const SCROLLBAR_PX = 12;

/** Shortest thumb that stays grabbable on a 5,000,000-row sheet. */
const MIN_THUMB_PX = 24;

/**
 * The kernel channels the grid reads and writes through.
 *
 * The three required members are what painting and resizing need. The optional ones
 * back the Shortcut_Handler: a host that supplies none of them still gets a grid that
 * scrolls, selects, and edits — every unavailable action reports itself instead of
 * failing silently — which is what keeps the benchmark and component fixtures small.
 */
export interface GridDataSource {
  view(range: Range): Promise<ViewTile>;
  sheetView(sheet: SheetId): Promise<SheetView>;
  dispatch(command: Command): Promise<CommandResult>;
  /** One batch, one history entry: a range fill must undo in one step (R12.1). */
  dispatchBatch?(commands: readonly Command[], label?: string): Promise<CommandResult>;
  /** Typed content of one cell, for `F2` and for the fill source row/column. */
  cellContent?(at: Addr): Promise<CellContent>;
  /** `Ctrl+Arrow` target, resolved in the worker (Requirements 3.2, 3.3). */
  blockEdge?(at: Addr, dir: Direction): Promise<Addr>;
  /** `Ctrl+End` target: the used-range corner (Requirement 3.5). */
  lastUsedCell?(sheet: SheetId): Promise<Addr>;
  undo?(): Promise<unknown>;
  redo?(): Promise<unknown>;
}

export interface GridViewOptions {
  source: GridDataSource;
  sheet: SheetId;
  /** Fired whenever the active cell or the selected rectangle changes. */
  onSelectionChange?: (selection: SelectionState) => void;
  /** Fired with a user-facing notice (a rejected command, mostly). */
  onMessage?: (text: string) => void;
  /** Fired after a sheet's geometry is (re)loaded. */
  onSheetView?: (view: SheetView) => void;
  /** Fired after each painted frame. Used by the scroll benchmark. */
  onFrame?: (stats: PaintStats) => void;
  /**
   * Fired for a shortcut the grid does not own — save, find, clipboard, sheet
   * switching, the format dialog, the cheat sheet. Return `true` when handled;
   * anything else reports the action as unavailable (Requirement 3.6 still holds,
   * because the browser default was already suppressed).
   */
  onAction?: (action: ShortcutAction) => boolean | void;
  /** Fired when Escape abandons an edit, with the content the cell keeps (R2.4). */
  onEditCancel?: (restore: string) => void;
  /** Fired when a column header (A, B, C...) is clicked. */
  onColumnHeaderClick?: (col: number, rect: { x: number; y: number; w: number; h: number }) => void;
}

export interface GridView {
  element: HTMLElement;
  /** The focusable, screen-reader-visible grid surface. */
  surface: HTMLElement;
  /** Points the view at another sheet and loads its geometry (Requirement 1.12). */
  setSheet(sheet: SheetId): Promise<SheetView>;
  /** Reloads the current sheet's geometry, freeze, merges, and used range. */
  reload(): Promise<SheetView>;
  sheet(): SheetId;
  sheetView(): SheetView | null;
  selection(): SelectionState;
  /** Selects a cell (or extends to it) and scrolls it into view. */
  select(at: Addr, extend?: boolean): void;
  /** Selects a rectangle outright, as the name box does. */
  selectRange(range: Range): void;
  /** Drops cached tiles intersecting `regions` and repaints (from `changedRegions`). */
  invalidate(regions: readonly Range[]): void;
  /** Freezes at the active cell, or unfreezes when already frozen there (R1.8). */
  toggleFreeze(): Promise<void>;
  /** Toggles filter icons visibility on Row 1 */
  setFilterIconsVisible(visible: boolean): void;
  isFilterIconsVisible(): boolean;
  /** The in-cell editor overlay (Requirements 2.1–2.5). */
  editor: CellEditor;
  /** True while a cell is being edited, which is `edit` keyboard mode. */
  isEditing(): boolean;
  /** Opens the editor: with `seed` it replaces the content (R2.1), without it, F2. */
  beginEdit(seed?: string): void;
  /** Confirms the open edit (Requirements 2.2, 2.3, 3.1). */
  commitEdit(key: CommitKey): Promise<void>;
  /** Abandons the open edit, leaving the cell as it was (Requirement 2.4). */
  cancelEdit(): void;
  /** Runs one shortcut action, as the binding table dispatches it. */
  runAction(action: ShortcutAction): void;
  /** Ranges highlighted for the formula being edited (Requirement 2.9). */
  highlights(): Range[];
  /** Waits for every tile of the current viewport, then paints synchronously. */
  settle(): Promise<PaintStats | null>;
  /** Paints one frame now, outside the animation loop. */
  paintNow(): PaintStats | null;
  requestPaint(): void;
  setPaintPaused(paused: boolean): void;
  isPaintPaused(): boolean;
  focus(): void;
  cache(): TileCache;
  dispose(): void;
}

interface PaneSizeState {
  widthPx: number;
  heightPx: number;
}

function emptySheetAxes(): { rowAxis: Axis; colAxis: Axis } {
  return {
    rowAxis: createAxis(20, [], MAX_ROWS),
    colAxis: createAxis(100, [], MAX_COLS),
  };
}

export function createGridView(options: GridViewOptions): GridView {
  const { source } = options;

  const canvas = el('canvas', {
    className: 'grid-canvas',
    testid: 'grid-canvas',
    attrs: { 'aria-hidden': 'true' },
  });
  const surface = el('div', {
    className: 'grid-surface',
    testid: 'grid',
    role: 'grid',
    ariaLabel: 'Spreadsheet grid',
    attrs: {
      tabindex: '0',
      'aria-rowcount': String(MAX_ROWS),
      'aria-colcount': String(MAX_COLS),
    },
  });
  const announcer = el('div', {
    className: 'sr-only',
    testid: 'active-cell',
    role: 'status',
    attrs: { 'aria-live': 'polite' },
  });
  const vTrack = el('div', {
    className: 'scroll-track scroll-v',
    attrs: { 'aria-hidden': 'true' },
  });
  const vThumb = el('div', { className: 'scroll-thumb', testid: 'vscroll-thumb' });
  vTrack.appendChild(vThumb);
  const hTrack = el('div', {
    className: 'scroll-track scroll-h',
    attrs: { 'aria-hidden': 'true' },
  });
  const hThumb = el('div', { className: 'scroll-thumb', testid: 'hscroll-thumb' });
  hTrack.appendChild(hThumb);

  const element = el('div', { className: 'grid-view' }, [
    canvas,
    surface,
    announcer,
    vTrack,
    hTrack,
  ]);


  let sheet = options.sheet;
  let view: SheetView | null = null;
  let axes = emptySheetAxes();
  let scroll = { x: 0, y: 0 };
  let selection = selectCell({ sheet, row: 0, col: 0 });
  let pane: PaneSizeState = { widthPx: 640, heightPx: 400 };
  let frame: number | null = null;
  let disposed = false;
  let drag: ResizeDrag | null = null;
  let dragOverride: [number, number] | null = null;
  let selecting = false;
  /** An in-progress fill-handle drag: the seed block and where it has reached. */
  let fillDrag: { seed: Range; target: Range } | null = null;
  let thumbDrag: { axis: 'x' | 'y'; startPointer: number; startScroll: number } | null = null;
  /**
   * An in-progress point-mode gesture: a click on the grid that is writing a reference
   * into the formula being edited, rather than selecting a cell.
   *
   * `head` and `tail` bracket the reference in the formula text, so dragging can
   * rewrite it in place as the pointer moves without re-deriving the caret position.
   */
  let pointing: { anchor: Addr; head: string; tail: string } | null = null;

  /** Typed content of the active cell, kept fresh so `F2` opens without a wait. */
  let showFilterIcons = false;
  let activeInput = '';
  let activeInputFor: Addr | null = null;

  const editor: CellEditor = createCellEditor({
    onKeyDown: (event: KeyboardEvent) => {
      onKeyDown(event);
    },
    onChange: () => {
      // The reference highlight follows the buffer (Requirement 2.9).
      requestPaint();
    },
    onBlur: () => {
      // A point-mode click must not end the edit. The click is prevented from moving
      // focus, but guarding here too means no ordering surprise can commit a formula
      // the user is still building.
      if (pointing !== null) return;
      if (editor.isOpen() && !isCommitting) {
        void commitEdit('ctrlEnter');
      }
    },
  });
  // Appended last so the overlay paints above the canvas and the focus surface.
  element.appendChild(editor.element);

  const cache = createTileCache({
    source,
    onTile: () => {
      requestPaint();
    },
    onError: (error) => {
      options.onMessage?.(
        `tile request failed: ${error instanceof Error ? error.message : String(error)}`,
      );
    },
  });

  function context(): GridContext2D | null {
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    const rawDpr = typeof window !== 'undefined' && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    const dpr = Math.min(Math.max(rawDpr, 1), 2);
    const width = Math.max(1, Math.round((pane.widthPx + ROW_HEADER_WIDTH_PX) * dpr));
    const height = Math.max(1, Math.round((pane.heightPx + COL_HEADER_HEIGHT_PX) * dpr));
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    return ctx as unknown as GridContext2D;
  }

  function measure(): void {
    const width = element.clientWidth;
    const height = element.clientHeight;
    if (width > 0 && height > 0) {
      pane = {
        widthPx: Math.max(0, width - ROW_HEADER_WIDTH_PX - SCROLLBAR_PX),
        heightPx: Math.max(0, height - COL_HEADER_HEIGHT_PX - SCROLLBAR_PX),
      };
    }
    canvas.style.width = `${pane.widthPx + ROW_HEADER_WIDTH_PX}px`;
    canvas.style.height = `${pane.heightPx + COL_HEADER_HEIGHT_PX}px`;
  }

  /** The axes, with the in-flight resize preview applied. */
  function currentAxes(): { rowAxis: Axis; colAxis: Axis } {
    if (drag === null || dragOverride === null) return axes;
    if (drag.kind === 'col') {
      const overrides: [number, number][] = [
        ...pairsOf(view?.colWidths ?? []),
        dragOverride,
      ];
      return {
        rowAxis: axes.rowAxis,
        colAxis: createAxis(axes.colAxis.defaultPx, overrides, MAX_COLS),
      };
    }
    const overrides: [number, number][] = [...pairsOf(view?.rowHeights ?? []), dragOverride];
    return {
      // Hidden rows travel with the preview axis: resizing a row must not un-hide the
      // rows a filter is hiding (Requirement 7.4).
      rowAxis: createAxis(axes.rowAxis.defaultPx, overrides, MAX_ROWS, view?.hiddenRows ?? []),
      colAxis: axes.colAxis,
    };
  }

  function pairsOf(source_: readonly (readonly [number, number])[]): [number, number][] {
    return source_.map(([index, px]) => [index, px] as [number, number]);
  }

  function updateScrollbars(rowAxis: Axis, colAxis: Axis): void {
    const vExtent = Math.max(1, pane.heightPx);
    const hExtent = Math.max(1, pane.widthPx);
    const vTotal = totalPx(rowAxis);
    const hTotal = totalPx(colAxis);
    const vSize = Math.max(MIN_THUMB_PX, (vExtent / Math.max(vExtent, vTotal)) * vExtent);
    const hSize = Math.max(MIN_THUMB_PX, (hExtent / Math.max(hExtent, hTotal)) * hExtent);
    const vFree = Math.max(0, vExtent - vSize);
    const hFree = Math.max(0, hExtent - hSize);
    vThumb.style.height = `${vSize}px`;
    vThumb.style.transform = `translateY(${thumbFraction(scroll.y, vTotal, vExtent) * vFree}px)`;
    hThumb.style.width = `${hSize}px`;
    hThumb.style.transform = `translateX(${thumbFraction(scroll.x, hTotal, hExtent) * hFree}px)`;
  }

  /** Pixel rectangle of one cell, freeze accounted for, for the editor overlay. */
  function rectOf(at: Addr): EditorRect {
    const { rowAxis, colAxis } = currentAxes();
    const freeze = view?.freeze ?? null;
    const rowFrozen = freeze !== null && at.row < freeze.row;
    const colFrozen = freeze !== null && at.col < freeze.col;
    return {
      x: ROW_HEADER_WIDTH_PX + offsetOf(colAxis, at.col) - (colFrozen ? 0 : scroll.x),
      y: COL_HEADER_HEIGHT_PX + offsetOf(rowAxis, at.row) - (rowFrozen ? 0 : scroll.y),
      w: sizeOf(colAxis, at.col),
      h: sizeOf(rowAxis, at.row),
    };
  }

  /**
   * Ranges the edited formula refers to, in this sheet's coordinates (R2.9).
   *
   * A cross-sheet reference (`Sheet2!A1`) is dropped rather than drawn on the wrong
   * sheet: the highlight carries the sheet *name* it was written with, and only
   * unqualified references or references to the sheet on screen belong here.
   */
  function highlights(): Range[] {
    if (!editor.isOpen()) return [];
    const name = view?.name ?? null;
    return editor
      .hints()
      .highlights.filter(
        (h) =>
          h.sheetName === null ||
          (name !== null && h.sheetName.toLowerCase() === name.toLowerCase()),
      )
      .map((h) => h.range);
  }

  function paintNow(): PaintStats | null {
    if (disposed) return null;
    measure();
    const ctx = context();
    const { rowAxis, colAxis } = currentAxes();
    const { visible, render } = computeVisibleRange(
      sheet,
      rowAxis,
      colAxis,
      { scrollX: scroll.x, scrollY: scroll.y, widthPx: pane.widthPx, heightPx: pane.heightPx },
      { rows: DEFAULT_BUFFER_ROWS, cols: DEFAULT_BUFFER_COLS },
    );
    // Tiles are fetched per visible span, not for the whole index range: with a filter
    // active the render range can straddle a million hidden rows, and requesting tiles
    // across the gap would ask the worker for thousands of tiles nobody paints.
    for (const [r0, r1] of visibleSegments(rowAxis, render.r0, render.r1)) {
      cache.ensure({ ...render, r0, r1 });
    }
    updateScrollbars(rowAxis, colAxis);
    // The overlay follows the cell it edits through scrolls and resizes.
    if (editor.isOpen()) editor.place(rectOf(selection.active));
    if (ctx === null) return null;
    const stats = paintGrid(ctx, {
      sheet,
      rowAxis,
      colAxis,
      scrollX: scroll.x,
      scrollY: scroll.y,
      widthPx: pane.widthPx + ROW_HEADER_WIDTH_PX,
      heightPx: pane.heightPx + COL_HEADER_HEIGHT_PX,
      visible,
      active: selection.active,
      anchor: selection.anchor,
      selection: selection.range,
      merges: view?.merges ?? [],
      freeze: view?.freeze ?? null,
      highlights: highlights(),
      showFilterIcons,
      cache,
    });
    options.onFrame?.(stats);
    return stats;
  }

  let paintPaused = false;

  function requestPaint(): void {
    if (disposed || paintPaused || frame !== null) return;
    if (typeof requestAnimationFrame !== 'function') {
      paintNow();
      return;
    }
    frame = requestAnimationFrame(() => {
      frame = null;
      if (!paintPaused) paintNow();
    });
  }

  function announce(): void {
    announcer.textContent = `${addrToA1(selection.active)}`;
    surface.setAttribute('aria-rowindex', String(selection.active.row + 1));
    surface.setAttribute('aria-colindex', String(selection.active.col + 1));
  }

  /**
   * Says something in the live region, replacing the plain cell announcement.
   *
   * Used for the events a screen-reader user cannot see happen: an editor opening, a
   * commit landing, a cancel discarding what was typed. The next selection change
   * puts the address back, so the region never goes stale.
   */
  function say(text: string): void {
    announcer.textContent = text;
  }

  /**
   * Keeps the active cell's typed content to hand.
   *
   * `F2` and type-to-edit both need the cell's *input* — the formula source, not the
   * formatted display — and waiting on a worker round trip after the keystroke would
   * make the editor open a frame late with the wrong text. So the content is fetched
   * when the active cell changes, once per cell rather than once per selection event.
   */
  function refreshActiveInput(): void {
    const fetch = source.cellContent;
    const at = selection.active;
    if (fetch === undefined) {
      activeInput = '';
      return;
    }
    if (
      activeInputFor !== null &&
      activeInputFor.sheet === at.sheet &&
      activeInputFor.row === at.row &&
      activeInputFor.col === at.col
    ) {
      return;
    }
    activeInputFor = at;
    void fetch(at).then(
      (content) => {
        if (activeInputFor === at) activeInput = content.input;
      },
      () => {
        if (activeInputFor === at) activeInput = '';
      },
    );
  }

  function publish(): void {
    announce();
    refreshActiveInput();
    options.onSelectionChange?.(selection);
    requestPaint();
  }

  function setSelection(next: SelectionState): void {
    selection = next;
    publish();
  }

  /** Selects (or extends to) a cell and scrolls the minimum distance to show it. */
  function selectAt(at: Addr, extend = false): void {
    const { rowAxis, colAxis } = currentAxes();
    scroll = scrollToShow(scroll, rowAxis, colAxis, pane, at.row, at.col);
    setSelection(extend ? extendTo(selection, at) : selectCell(at));
  }

  function scrollTo(next: { x: number; y: number }): void {
    const { rowAxis, colAxis } = currentAxes();
    scroll = clampScroll(next, rowAxis, colAxis, pane);
    requestPaint();
  }

  /* ------------------------------------------------------------- pointers */

  function localPoint(event: PointerEvent | MouseEvent | WheelEvent): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const targetW = pane.widthPx + ROW_HEADER_WIDTH_PX;
    const targetH = pane.heightPx + COL_HEADER_HEIGHT_PX;
    const scaleX = rect.width > 0 ? targetW / rect.width : 1;
    const scaleY = rect.height > 0 ? targetH / rect.height : 1;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    };
  }

  function hitContext(): Parameters<typeof hitTest>[0] {
    const { rowAxis, colAxis } = currentAxes();
    return {
      rowAxis,
      colAxis,
      scrollX: scroll.x,
      scrollY: scroll.y,
      freeze: view?.freeze ?? null,
    };
  }

  surface.addEventListener('pointerdown', (event: PointerEvent) => {
    const point = localPoint(event);
    if (editor.isOpen() && !isCommitting) {
      // Point mode first: while a formula is open, a click on a cell contributes that
      // cell to the formula and the edit continues. Only a click that cannot be read
      // that way — on a header, or with the caret somewhere a reference cannot go —
      // falls through to ending the edit.
      const hit = hitTest(hitContext(), point.x, point.y);
      if (hit.k === 'cell' && beginPointing({ sheet, row: hit.row, col: hit.col })) {
        // Prevents the focus change, which would blur the textarea and commit.
        event.preventDefault();
        surface.setPointerCapture(event.pointerId);
        return;
      }
      void commitEdit('ctrlEnter');
    }
    surface.focus();
    // The fill handle wins over the cell under it (Requirement 12.9): it sits on the
    // selection's bottom-right corner, and a pointer there means "extend the series",
    // not "select that one cell again".
    if (hitFillHandle(hitContext(), selection.range, point.x, point.y)) {
      fillDrag = { seed: normalizeRange(selection.range), target: normalizeRange(selection.range) };
      surface.setPointerCapture(event.pointerId);
      return;
    }
    const hit = hitTest(hitContext(), point.x, point.y);
    switch (hit.k) {
      case 'colResize': {
        const { colAxis } = currentAxes();
        drag = beginResize(hit, colAxis, point.x);
        dragOverride = [hit.col, drag.startSizePx];
        surface.setPointerCapture(event.pointerId);
        break;
      }
      case 'rowResize': {
        const { rowAxis } = currentAxes();
        drag = beginResize(hit, rowAxis, point.y);
        dragOverride = [hit.row, drag.startSizePx];
        surface.setPointerCapture(event.pointerId);
        break;
      }
      case 'colHeader': {
        setSelection(selectColumn(sheet, hit.col, MAX_ROWS - 1));
        const { colAxis } = currentAxes();
        const colX = ROW_HEADER_WIDTH_PX + offsetOf(colAxis, hit.col) - scroll.x;
        const colW = sizeOf(colAxis, hit.col);
        options.onColumnHeaderClick?.(hit.col, { x: colX, y: 0, w: colW, h: COL_HEADER_HEIGHT_PX });
        break;
      }
      case 'rowHeader':
        setSelection(selectRow(sheet, hit.row, MAX_COLS - 1));
        break;
      case 'cell': {
        const at: Addr = { sheet, row: hit.row, col: hit.col };
        selecting = true;
        surface.setPointerCapture(event.pointerId);
        setSelection(event.shiftKey ? extendTo(selection, at) : selectCell(at));
        if (hit.row === 0) {
          const { colAxis, rowAxis } = currentAxes();
          const colX = ROW_HEADER_WIDTH_PX + offsetOf(colAxis, hit.col) - scroll.x;
          const colW = sizeOf(colAxis, hit.col);
          const rowY = COL_HEADER_HEIGHT_PX + offsetOf(rowAxis, 0) - scroll.y;
          const rowH = sizeOf(rowAxis, 0);
          options.onColumnHeaderClick?.(hit.col, { x: colX, y: rowY, w: colW, h: rowH });
        }
        break;
      }
      case 'corner':
        setSelection({
          active: { sheet, row: 0, col: 0 },
          anchor: { sheet, row: 0, col: 0 },
          range: { sheet, r0: 0, c0: 0, r1: MAX_ROWS - 1, c1: MAX_COLS - 1 },
        });
        break;
    }
  });

  surface.addEventListener('pointermove', (event: PointerEvent) => {
    const point = localPoint(event);
    if (pointing !== null) {
      const hit = hitTest(hitContext(), point.x, point.y);
      if (hit.k === 'cell') extendPointing({ sheet, row: hit.row, col: hit.col });
      return;
    }
    if (fillDrag !== null) {
      const hit = hitTest(hitContext(), point.x, point.y);
      if (hit.k === 'cell') {
        // Previewed as a selection while the pointer moves; the command goes out once,
        // on release, so one drag is one history entry.
        fillDrag = { ...fillDrag, target: fillHandleTarget(fillDrag.seed, hit.row, hit.col) };
        setSelection({ ...selection, range: fillDrag.target });
      }
      return;
    }
    if (drag !== null) {
      const pointer = drag.kind === 'col' ? point.x : point.y;
      dragOverride = [drag.index, resizeSize(drag, pointer)];
      requestPaint();
      return;
    }
    if (selecting) {
      const hit = hitTest(hitContext(), point.x, point.y);
      if (hit.k === 'cell') {
        setSelection(extendTo(selection, { sheet, row: hit.row, col: hit.col }));
      }
      return;
    }
    surface.style.cursor = cursorFor(hitTest(hitContext(), point.x, point.y));
  });

  surface.addEventListener('pointerup', (event: PointerEvent) => {
    if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId);
    if (pointing !== null) {
      // The gesture is over; the edit is not. Focus never left the textarea, so typing
      // carries straight on from the reference that was just written.
      pointing = null;
      editor.sync();
      return;
    }
    selecting = false;
    const pendingFill = fillDrag;
    fillDrag = null;
    if (pendingFill !== null) {
      // A drag that never left the selection is a click on the handle: nothing to fill.
      if (fillDirection(pendingFill.seed, pendingFill.target) !== null) {
        void fill(pendingFill.seed, pendingFill.target);
      }
      return;
    }
    const active = drag;
    const override = dragOverride;
    drag = null;
    dragOverride = null;
    if (active === null || override === null) return;
    const command: Command =
      active.kind === 'col'
        ? { k: 'resizeCol', sheet, col: override[0], px: override[1] }
        : { k: 'resizeRow', sheet, row: override[0], px: override[1] };
    void source
      .dispatch(command)
      .then(async (result) => {
        if (!result.ok) {
          options.onMessage?.(result.error.message);
          return;
        }
        await reload();
      })
      .catch((error: unknown) => {
        options.onMessage?.(error instanceof Error ? error.message : String(error));
      });
  });

  surface.addEventListener(
    'wheel',
    (event: WheelEvent) => {
      event.preventDefault();
      const { rowAxis, colAxis } = currentAxes();
      const mode = (event.deltaMode === 1 ? 1 : event.deltaMode === 2 ? 2 : 0) as 0 | 1 | 2;
      scroll = scrollByWheel(scroll, rowAxis, colAxis, pane, {
        x: event.deltaX,
        y: event.deltaY,
        mode,
      });
      requestPaint();
    },
    { passive: false },
  );

  function startThumbDrag(axis: 'x' | 'y', event: PointerEvent): void {
    thumbDrag = {
      axis,
      startPointer: axis === 'y' ? event.clientY : event.clientX,
      startScroll: axis === 'y' ? scroll.y : scroll.x,
    };
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
  }

  vThumb.addEventListener('pointerdown', (event: PointerEvent) => {
    event.preventDefault();
    startThumbDrag('y', event);
  });
  hThumb.addEventListener('pointerdown', (event: PointerEvent) => {
    event.preventDefault();
    startThumbDrag('x', event);
  });

  function moveThumb(event: PointerEvent): void {
    if (thumbDrag === null) return;
    const { rowAxis, colAxis } = currentAxes();
    if (thumbDrag.axis === 'y') {
      const extent = Math.max(1, pane.heightPx);
      const free = Math.max(1, extent - MIN_THUMB_PX);
      const fraction =
        thumbFraction(thumbDrag.startScroll, totalPx(rowAxis), extent) +
        (event.clientY - thumbDrag.startPointer) / free;
      scrollTo({ x: scroll.x, y: scrollFromThumb(fraction, totalPx(rowAxis), extent) });
      return;
    }
    const extent = Math.max(1, pane.widthPx);
    const free = Math.max(1, extent - MIN_THUMB_PX);
    const fraction =
      thumbFraction(thumbDrag.startScroll, totalPx(colAxis), extent) +
      (event.clientX - thumbDrag.startPointer) / free;
    scrollTo({ x: scrollFromThumb(fraction, totalPx(colAxis), extent), y: scroll.y });
  }

  vThumb.addEventListener('pointermove', moveThumb);
  hThumb.addEventListener('pointermove', moveThumb);
  const endThumb = (event: PointerEvent): void => {
    if (thumbDrag === null) return;
    thumbDrag = null;
    const target = event.target as HTMLElement;
    if (target.hasPointerCapture(event.pointerId)) target.releasePointerCapture(event.pointerId);
  };
  vThumb.addEventListener('pointerup', endThumb);
  hThumb.addEventListener('pointerup', endThumb);

  vTrack.addEventListener('pointerdown', (event: PointerEvent) => {
    if (event.target !== vTrack) return;
    const rect = vTrack.getBoundingClientRect();
    const { rowAxis } = currentAxes();
    scrollTo({
      x: scroll.x,
      y: scrollFromThumb(
        (event.clientY - rect.top) / Math.max(1, rect.height),
        totalPx(rowAxis),
        Math.max(1, pane.heightPx),
      ),
    });
  });
  hTrack.addEventListener('pointerdown', (event: PointerEvent) => {
    if (event.target !== hTrack) return;
    const rect = hTrack.getBoundingClientRect();
    const { colAxis } = currentAxes();
    scrollTo({
      x: scrollFromThumb(
        (event.clientX - rect.left) / Math.max(1, rect.width),
        totalPx(colAxis),
        Math.max(1, pane.widthPx),
      ),
      y: scroll.y,
    });
  });

  /* ------------------------------------------------------------- keyboard */

  /** The keyboard mode the binding table is consulted in (Requirement 3.1). */
  function mode(): ShortcutMode {
    return editor.isOpen() ? 'edit' : 'grid';
  }

  /**
   * One keyboard entry point for both modes.
   *
   * Order matters: the binding table first — it suppresses the browser default for
   * everything it matches (Requirement 3.6) — then the plain viewport-moving keys of
   * task 11, then type-to-edit (Requirement 2.1). A key the table does not claim in
   * `edit` mode is ordinary typing and is left to the textarea.
   */
  function onKeyDown(event: KeyboardEvent): void {
    const current = mode();
    const binding = handleKey(event, current);
    if (binding !== null) {
      runAction(binding.command);
      return;
    }
    if (current === 'edit') return;
    if (isNavKey(event.key) && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const { rowAxis, colAxis } = currentAxes();
      const used = view?.usedRange;
      const result = navigate(selection, scroll, rowAxis, colAxis, pane, event.key, {
        extend: event.shiftKey,
        ...(used === undefined ? {} : { usedRange: used }),
      });
      scroll = result.scroll;
      setSelection(result.selection);
      return;
    }
    if (isTypeToEdit(event)) {
      event.preventDefault();
      beginEdit(event.key);
    }
  }

  surface.addEventListener('keydown', onKeyDown);

  surface.addEventListener('dblclick', (event: MouseEvent) => {
    const point = localPoint(event);
    const hit = hitTest(hitContext(), point.x, point.y);
    if (hit.k !== 'cell') return;
    event.preventDefault();
    selectAt({ sheet, row: hit.row, col: hit.col });
    beginEdit();
  });

  /* --------------------------------------------------------------- point mode */

  /**
   * Starts writing a pointed reference into the formula being edited.
   *
   * Returns false when the click is not a reference gesture — the text is not a
   * formula, or the caret is somewhere a reference cannot go — and the caller then
   * treats the click as an ordinary selection, ending the edit as before.
   */
  function beginPointing(at: Addr): boolean {
    const session = editor.sync();
    if (session === null) return false;
    const ref = addrToA1(at);
    const pointed = pointReference(session.text, editor.caret(), ref);
    if (pointed === null) return false;
    pointing = {
      anchor: at,
      head: pointed.text.slice(0, pointed.start),
      tail: pointed.text.slice(pointed.start + ref.length),
    };
    editor.setText(pointed.text, pointed.caret);
    // The reference highlight is drawn from the buffer, so the pointed cell outlines
    // itself as soon as the text changes (Requirement 2.9).
    requestPaint();
    return true;
  }

  /** Extends the pointed reference to a range as the pointer is dragged. */
  function extendPointing(to: Addr): void {
    if (pointing === null) return;
    const { anchor, head, tail } = pointing;
    const box = normalizeRange({
      sheet: anchor.sheet,
      r0: anchor.row,
      c0: anchor.col,
      r1: to.row,
      c1: to.col,
    });
    // A drag that has not left the anchor is still a single-cell reference; printing a
    // degenerate `A1:A1` range would be technically right and visibly wrong.
    const ref =
      box.r0 === box.r1 && box.c0 === box.c1 ? addrToA1(anchor) : rangeToA1(box);
    editor.setText(head + ref + tail, head.length + ref.length);
    requestPaint();
  }

  /* ------------------------------------------------------------- cell editing */

  /** Opens the editor (Requirements 2.1, 3.1). `seed` replaces the content. */
  function beginEdit(seed?: string): void {
    const at = selection.active;
    editor.open(
      seed === undefined
        ? newEditSession(at, activeInput, { intent: 'edit' })
        : newEditSession(at, activeInput, { intent: 'type', seed }),
      rectOf(at),
    );
    say(`Editing ${addrToA1(at)}`);
    requestPaint();
  }

  let isCommitting = false;

  /**
   * Confirms an edit (Requirements 2.2, 2.3, 3.1).
   *
   * The command comes from `commitPlan`, so the typed text reaches the dispatcher
   * unclassified and `classifyInput` decides what it is exactly once. A range fill
   * goes through `dispatchBatch` so the whole fill undoes in one step.
   */
  async function commitEdit(key: CommitKey): Promise<void> {
    if (isCommitting) return;
    isCommitting = true;
    try {
      const session = editor.sync();
      if (session === null) return;
      const plan = commitPlan(session, key, selection.range);
      editor.close();
      surface.focus();
      if (plan.notice !== null) options.onMessage?.(plan.notice);
      const batch = source.dispatchBatch;
      const result =
        plan.filled !== null && batch !== undefined
          ? await batch([plan.command], 'Fill range')
          : await source.dispatch(plan.command);
      if (!result.ok) {
        options.onMessage?.(result.error.message);
        say(`${addrToA1(session.addr)} not changed: ${result.error.message}`);
        return;
      }
      // A fill stays on the cell that was edited; every other commit moves.
      if (plan.filled === null) selectAt(plan.next);
      else publish();
      // After the selection has been published, so the announcement is the last word
      // rather than being overwritten by the address of the cell just moved to.
      const filled = plan.filled;
      say(
        filled === null
          ? `${addrToA1(session.addr)} updated, ${addrToA1(plan.next)} active`
          : `Filled ${(filled.r1 - filled.r0 + 1) * (filled.c1 - filled.c0 + 1)} cells`,
      );
    } catch (err) {
      options.onMessage?.(err instanceof Error ? err.message : String(err));
    } finally {
      isCommitting = false;
    }
  }

  /** Abandons an edit. Nothing is dispatched, so the cell keeps its content (R2.4). */
  function cancelEdit(): void {
    const session = editor.session();
    editor.close();
    surface.focus();
    if (session === null) return;
    const { restore } = cancelEditSession(session);
    options.onEditCancel?.(restore);
    publish();
    say(`Edit cancelled, ${addrToA1(session.addr)} unchanged`);
  }

  /* ------------------------------------------------------------ shortcut actions */

  async function report(command: Command): Promise<CommandResult> {
    const result = await source.dispatch(command);
    if (!result.ok) options.onMessage?.(result.error.message);
    return result;
  }

  function delegate(action: ShortcutAction): void {
    if (options.onAction?.(action) === true) return;
    options.onMessage?.(`The ${action} shortcut is not available yet.`);
  }

  function step(dRow: number, dCol: number): void {
    const at = selection.active;
    selectAt({
      sheet,
      row: Math.max(0, Math.min(at.row + dRow, MAX_ROWS - 1)),
      col: Math.max(0, Math.min(at.col + dCol, MAX_COLS - 1)),
    });
  }

  /** `Ctrl+Arrow` and `Ctrl+Shift+Arrow` (Requirements 3.2, 3.3). */
  async function blockMove(nav: BlockNav): Promise<void> {
    const resolve = source.blockEdge;
    if (resolve === undefined) {
      options.onMessage?.('Block-edge navigation is unavailable.');
      return;
    }
    selectAt(await resolve(selection.active, nav.dir), nav.extend);
  }

  /** `Ctrl+End` (Requirement 3.5). */
  async function gotoLastCell(): Promise<void> {
    const resolve = source.lastUsedCell;
    if (resolve === undefined) {
      const used = view?.usedRange;
      selectAt({ sheet, row: Math.max(0, used?.r1 ?? 0), col: Math.max(0, used?.c1 ?? 0) });
      return;
    }
    selectAt(await resolve(sheet));
  }

  /** `Ctrl+B` / `Ctrl+I` / `Ctrl+U`: toggled against the active cell's own style. */
  async function toggleFont(kind: 'bold' | 'italic' | 'underline'): Promise<void> {
    const cell = cache.cell(sheet, selection.active.row, selection.active.col);
    const style = cell === null ? null : styleOf(cell.tile, cell.styleRef);
    const on = style === null ? false : style[kind];
    const patch: CellStylePatch =
      kind === 'bold' ? { bold: !on } : kind === 'italic' ? { italic: !on } : { underline: !on };
    await report({ k: 'applyStyle', range: selection.range, patch });
  }

  /** `Ctrl+;`: today's date as typed text, so `classifyInput` types and formats it. */
  async function insertToday(): Promise<void> {
    const now = new Date();
    const pad = (n: number): string => String(n).padStart(2, '0');
    const input = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    await report({ k: 'setCell', addr: selection.active, input });
  }

  /**
   * `Ctrl+D` and `Ctrl+R` (Requirements 3.1, 12.9).
   *
   * The first row (or column) of the selection is the seed and the rest of the
   * selection is the target, which is exactly the `fill` command's shape. The series
   * detection happens in the kernel, where the seed values live — so the grid sends
   * one small command instead of reading the seeds back and computing here, and the
   * whole fill is one undo entry however many cells it wrote.
   */
  async function fillAlong(dir: 'down' | 'right'): Promise<void> {
    const r = normalizeRange(selection.range);
    const rows = r.r1 - r.r0 + 1;
    const cols = r.c1 - r.c0 + 1;
    if ((dir === 'down' && rows < 2) || (dir === 'right' && cols < 2)) {
      options.onMessage?.(
        dir === 'down'
          ? 'Select at least two rows to fill down.'
          : 'Select at least two columns to fill right.',
      );
      return;
    }
    const from: Range = dir === 'down' ? { ...r, r1: r.r0 } : { ...r, c1: r.c0 };
    await fill(from, r);
  }

  /**
   * Continues a series from `from` across `to` (Requirement 12.9).
   *
   * Shared by `Ctrl+D`, `Ctrl+R`, and the fill-handle drag, so all three produce the
   * same command and the same single history entry.
   */
  async function fill(from: Range, to: Range): Promise<void> {
    const result = await report({ k: 'fill', from, to });
    if (result.ok) setSelection({ ...selection, range: normalizeRange(to) });
  }

  /** Runs one binding's action. Unowned actions go to `onAction`. */
  function runAction(action: ShortcutAction): void {
    const nav = blockNavOf(action);
    if (nav !== null) {
      void blockMove(nav);
      return;
    }
    const commit = commitKeyOf(action);
    if (commit !== null) {
      void commitEdit(commit);
      return;
    }
    const used = view?.usedRange;
    switch (action) {
      case 'editCell':
        beginEdit();
        return;
      case 'cancelEdit':
        cancelEdit();
        return;
      case 'lineBreak':
        editor.lineBreak();
        return;
      case 'cycleAnchor':
        if (!editor.isOpen()) {
          options.onMessage?.('F4 cycles reference anchoring while a formula is being edited.');
          return;
        }
        if (!editor.cycleAnchor()) options.onMessage?.('No cell reference under the caret.');
        return;
      case 'moveDown':
        step(1, 0);
        return;
      case 'moveUp':
        step(-1, 0);
        return;
      case 'moveRight':
        step(0, 1);
        return;
      case 'moveLeft':
        step(0, -1);
        return;
      case 'homeCell':
        selectAt({ sheet, row: 0, col: 0 });
        return;
      case 'lastCell':
        void gotoLastCell();
        return;
      case 'clearValues':
        void report({ k: 'clearValues', range: selection.range });
        return;
      case 'selectAll':
        setSelection({
          active: { sheet, row: 0, col: 0 },
          anchor: { sheet, row: 0, col: 0 },
          range: {
            sheet,
            r0: 0,
            c0: 0,
            r1: Math.max(0, used?.r1 ?? 0),
            c1: Math.max(0, used?.c1 ?? 0),
          },
        });
        return;
      // `Ctrl+Space` and `Shift+Space` keep the active cell where it is, so pressing
      // one after the other selects the column *and then the row the user was on*,
      // which is what Excel does. A header click is different — it moves the active
      // cell to the head of the column or row — and that stays in `interaction.ts`.
      case 'selectColumn': {
        const active = selection.active;
        setSelection({
          active,
          anchor: active,
          range: {
            sheet,
            r0: 0,
            c0: active.col,
            r1: MAX_ROWS - 1,
            c1: active.col,
          },
        });
        return;
      }
      case 'selectRow': {
        const active = selection.active;
        setSelection({
          active,
          anchor: active,
          range: {
            sheet,
            r0: active.row,
            c0: 0,
            r1: active.row,
            c1: MAX_COLS - 1,
          },
        });
        return;
      }
      case 'insertRowAndCol': {
        const r = normalizeRange(selection.range);
        const isFullCol = r.r0 === 0 && r.r1 >= MAX_ROWS - 1;
        const isFullRow = r.c0 === 0 && r.c1 >= MAX_COLS - 1;
        const countCols = r.c1 - r.c0 + 1;
        const countRows = r.r1 - r.r0 + 1;
        if (isFullCol) {
          void report({ k: 'insertCols', sheet, at: r.c0, count: countCols }).then(() => reload());
        } else if (isFullRow) {
          void report({ k: 'insertRows', sheet, at: r.r0, count: countRows }).then(() => reload());
        } else {
          void report({ k: 'insertRows', sheet, at: r.r0, count: countRows }).then(() => reload());
        }
        return;
      }
      case 'deleteRowAndCol': {
        const r = normalizeRange(selection.range);
        const isFullCol = r.r0 === 0 && r.r1 >= MAX_ROWS - 1;
        const isFullRow = r.c0 === 0 && r.c1 >= MAX_COLS - 1;
        const countCols = r.c1 - r.c0 + 1;
        const countRows = r.r1 - r.r0 + 1;
        if (isFullCol) {
          void report({ k: 'deleteCols', sheet, at: r.c0, count: countCols }).then(() => reload());
        } else if (isFullRow) {
          void report({ k: 'deleteRows', sheet, at: r.r0, count: countRows }).then(() => reload());
        } else {
          // A rectangular selection contains both row and column intent.
          // Delete both axes in one worker batch so Ctrl+- is a single action
          // (and a single undo entry) instead of silently deleting rows only.
          const deleteBatch = source.dispatchBatch;
          if (deleteBatch !== undefined) {
            void deleteBatch(
              [
                { k: 'deleteRows', sheet, at: r.r0, count: countRows },
                { k: 'deleteCols', sheet, at: r.c0, count: countCols },
              ],
              'Delete selected rows and columns',
            ).then(() => reload());
          } else {
            void report({ k: 'deleteRows', sheet, at: r.r0, count: countRows }).then(() => reload());
          }
        }
        return;
      }
      case 'toggleBold':
        void toggleFont('bold');
        return;
      case 'toggleItalic':
        void toggleFont('italic');
        return;
      case 'toggleUnderline':
        void toggleFont('underline');
        return;
      case 'insertDate':
        void insertToday();
        return;
      case 'fillDown':
        void fillAlong('down');
        return;
      case 'fillRight':
        void fillAlong('right');
        return;
      case 'undo': {
        const run = source.undo;
        if (run === undefined) {
          delegate(action);
          return;
        }
        void run();
        return;
      }
      case 'redo': {
        const run = source.redo;
        if (run === undefined) {
          delegate(action);
          return;
        }
        void run();
        return;
      }
      default:
        delegate(action);
        return;
    }
  }

  /* ---------------------------------------------------------------- sizing */

  const observer =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          requestPaint();
        })
      : null;
  observer?.observe(element);
  const onWindowResize = (): void => {
    requestPaint();
  };
  window.addEventListener('resize', onWindowResize);

  /* ------------------------------------------------------------ sheet load */

  async function load(next: SheetId): Promise<SheetView> {
    const loaded = await source.sheetView(next);
    sheet = loaded.sheet;
    view = loaded;
    axes = { rowAxis: rowAxisOf(loaded), colAxis: colAxisOf(loaded) };
    scroll = clampScroll(scroll, axes.rowAxis, axes.colAxis, pane);
    options.onSheetView?.(loaded);
    requestPaint();
    return loaded;
  }

  async function reload(): Promise<SheetView> {
    cache.clear();
    return load(sheet);
  }

  function selectRange(range: Range): void {
    const r = normalizeRange(range);
    const active: Addr = { sheet: r.sheet, row: r.r0, col: r.c0 };
    const { rowAxis, colAxis } = currentAxes();
    scroll = scrollToShow(scroll, rowAxis, colAxis, pane, active.row, active.col);
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(`numix_scroll_${active.sheet}`, JSON.stringify(scroll));
      }
    } catch {
      /* ignore */
    }
    setSelection({ active, anchor: active, range: r });
  }

  measure();
  announce();

  return {
    element,
    surface,

    async setSheet(next: SheetId): Promise<SheetView> {
      if (next !== sheet) {
        scroll = { x: 0, y: 0 };
        selection = selectCell({ sheet: next, row: 0, col: 0 });
      }
      const loaded = await load(next);
      publish();
      return loaded;
    },

    reload,
    sheet: () => sheet,
    sheetView: () => view,
    selection: () => selection,

    select: selectAt,

    selectRange,

    invalidate(regions: readonly Range[]): void {
      cache.invalidate(regions);
      requestPaint();
    },

    async toggleFreeze(): Promise<void> {
      const current = view?.freeze ?? null;
      const at = selection.active;
      const already =
        current !== null && current.row === at.row && current.col === at.col;
      const result = await source.dispatch({
        k: 'freeze',
        sheet,
        at: already ? null : { sheet, row: at.row, col: at.col },
      });
      if (!result.ok) {
        options.onMessage?.(result.error.message);
        return;
      }
      await reload();
      options.onMessage?.(
        already ? 'Panes unfrozen.' : `Panes frozen above and left of ${addrToA1(at)}.`,
      );
    },

    setFilterIconsVisible(visible: boolean): void {
      showFilterIcons = visible;
      requestPaint();
    },

    isFilterIconsVisible(): boolean {
      return showFilterIcons;
    },

    async settle(): Promise<PaintStats | null> {
      measure();
      const { rowAxis, colAxis } = currentAxes();
      const { render } = computeVisibleRange(
        sheet,
        rowAxis,
        colAxis,
        { scrollX: scroll.x, scrollY: scroll.y, widthPx: pane.widthPx, heightPx: pane.heightPx },
        { rows: DEFAULT_BUFFER_ROWS, cols: DEFAULT_BUFFER_COLS },
      );
      await cache.ready(render);
      return paintNow();
    },

    paintNow,
    requestPaint,
    setPaintPaused(paused: boolean): void {
      paintPaused = paused;
      if (!paused) requestPaint();
    },
    isPaintPaused(): boolean {
      return paintPaused;
    },

    editor,
    isEditing: () => editor.isOpen(),
    beginEdit,
    commitEdit,
    cancelEdit,
    runAction,
    highlights,

    focus(): void {
      surface.focus();
    },

    cache: () => cache,

    dispose(): void {
      disposed = true;
      editor.close();
      if (frame !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      frame = null;
      observer?.disconnect();
      window.removeEventListener('resize', onWindowResize);
      cache.clear();
    },
  };
}
